import { Box } from '@material-ui/core'
import { Button } from 'components/facets'
import { ActionCard } from 'components/facets/ActionCard'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BulkSettings, Competitor, Game, PriceStategyTarget, PricingStrategy } from 'shared'
import { StepBox } from '../StepBox'
import { CompetitorFlowPresenter } from './CompetitorFlowPresenter'
import { MinMaxFlowPresenter } from './MinMaxFlowPresenter'
import { PriceGuideFlowPresenter } from './PriceGuideFlowPresenter'

/**
 * Labels for a strategy's base kind. Shared with the strategy boxes so the
 * creation flow and the saved strategies always show the same wording.
 */
export const STRATEGY_KIND_LABELS: Record<PricingStrategy['base']['kind'], string> = {
  articleData: 'Competition',
  priceGuide: 'Price Guide',
  minMax: 'Min/Max',
}

export interface CreateStrategyPresenterProps {
  newName: string
  competitorNewName: string
  competitors: Competitor[]
  bulkSettings: BulkSettings[]
  onDone: (strategy: PricingStrategy) => void
  onCompetitorDone: (competitor: Competitor) => void
  competitionDisabled: boolean
  activeGame: Game
  target: PriceStategyTarget
}

export const CreateStrategyDialogContentPresenter = ({
  newName,
  bulkSettings,
  competitors,
  competitorNewName,
  onDone,
  onCompetitorDone,
  competitionDisabled,
  activeGame,
  target,
}: CreateStrategyPresenterProps): JSX.Element => {
  const [strategyKind, setStrategyKind] = useState<'articleData' | 'priceGuide' | 'minMax' | null>(
    null
  )
  const { t } = useTranslation()
  const titleKey = target === PriceStategyTarget.buyPrice ? 'titleBuy' : 'titleSell'
  const strategyKindLabel = strategyKind ? STRATEGY_KIND_LABELS[strategyKind] : null

  return (
    <Box width={1000}>
      <h4 className="mb-0">
        {t(`strategy.create.${titleKey}`)}
        {strategyKindLabel && ` — ${strategyKindLabel}`}
      </h4>
      {!strategyKind ? (
        <StepBox>
          <p className="mt-3 mb-4">{t('strategy.create.baseDescription')}</p>
          <div className="d-flex flex-column p-2">
            {(
              [
                {
                  kind: 'articleData',
                  title: STRATEGY_KIND_LABELS.articleData,
                  description: t('strategy.create.competitionDescription'),
                  iconClass: 'fa-light fa-circle-dollar',
                  disabled: competitionDisabled,
                  recommended: true,
                  testid: 'strategy-competition',
                },
                {
                  kind: 'priceGuide',
                  title: STRATEGY_KIND_LABELS.priceGuide,
                  description: t('strategy.create.priceGuideDescription'),
                  iconClass: 'fa-light fa-book-open-cover',
                  disabled: false,
                  recommended: false,
                  testid: 'strategy-price-guide',
                },
                {
                  kind: 'minMax',
                  title: STRATEGY_KIND_LABELS.minMax,
                  description: t('strategy.create.minMaxDescription'),
                  iconClass: 'fa-light fa-arrow-right-arrow-left',
                  disabled: false,
                  recommended: false,
                  testid: 'strategy-min-max',
                },
              ] as const
            ).map(({ kind, title, description, iconClass, disabled, recommended, testid }) => (
              <div key={kind} className="mb-4">
                <ActionCard
                  variant="withTitleIconDescription"
                  iconClass={iconClass}
                  title={`${title}${recommended ? ` (${t('common.recommended')})` : ''}`}
                  className="choose-pricing-strategy"
                  description={description}
                  button={
                    <Button
                      variant="contained"
                      disabled={disabled}
                      onClick={() => setStrategyKind(kind)}
                      data-testid={testid}
                    >
                      {t('common.select')}
                    </Button>
                  }
                />
              </div>
            ))}
          </div>
        </StepBox>
      ) : (
        {
          articleData: (
            <CompetitorFlowPresenter
              onCompetitorDone={onCompetitorDone}
              onDone={onDone}
              activeGame={activeGame}
              competitorNewName={competitorNewName}
              bulkSettings={bulkSettings}
              competitors={competitors}
              key={strategyKind}
              onRestart={() => setStrategyKind(null)}
              newName={newName}
            />
          ),
          priceGuide: (
            <PriceGuideFlowPresenter
              onDone={onDone}
              bulkSettings={bulkSettings}
              key={strategyKind}
              onRestart={() => setStrategyKind(null)}
              newName={newName}
              dataTestid={`strategy-price-guide`}
            />
          ),
          minMax: (
            <MinMaxFlowPresenter
              onDone={onDone}
              key={strategyKind}
              onRestart={() => setStrategyKind(null)}
              newName={newName}
              dataTestid={`strategy-min-max`}
            />
          ),
        }[strategyKind]
      )}
    </Box>
  )
}
